import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { SessionService } from '../session/session.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ClientWithOrigin } from '../../model/client-with-origin';
import {
  AccessTokens,
  ClientEvent,
  createClient,
  EventTimeline,
  ICreateClientOpts,
  LoginResponse,
  MatrixClient,
  MatrixEventEvent,
  MatrixScheduler,
  RegisterResponse,
  RoomEvent,
  SecretStorage,
  SyncState
} from 'matrix-js-sdk';
import { CryptoEvent } from 'matrix-js-sdk/lib/crypto-api';
import { User } from 'matrix-js-sdk/lib/models/user';
import { SchedulerService } from '../scheduler/scheduler.service';
import { VerificationMethod } from 'matrix-js-sdk/lib/types';
import { SecretStorageCallbacks } from 'matrix-js-sdk/lib/secret-storage';

import { DEFAULT_CONFIGURATION, Settings, SettingsService } from '../settings/settings.service';
import { AppConfigService } from '../config/app-config.service';

/**
 * Die MatrixService-Klasse verwaltet den globalen Matrix-Client.
 *
 * Folgende Funktionen sind umgesetzt:
 *
 * Zugriff auf den globalen Matrix-Client und Verwaltung seines Zustandes.
 * Login und Registrierung an einem Homeserver (auch ohne aktiven Client).
 * Initialisierung der Ende-zu-Ende-Verschlüsselung und Start der Synchronisation.
 * Verwaltung von Secret-Storage-Schlüsseln & Bereitstellung nach der Geräteverifikation.
 * Ergänzung des Headers 'X-TIM-User-Agent' und Durchführung eines Token-Refreshs.
 *
 * Umgesetzte Anforderung:
 *
 * A_25394 - Anforderung von refresh token durch den TI-M Client.
 * A_25559 - E2E erfüllt durch initCrypto
 */
@Injectable({ providedIn: 'root' })
export class MatrixService {
  // Wert des Headers *X-TIM-User-Agent*,
  // A_25483-01 - TI-M Client User-Agent
  // der von {@link createTimFetch} an jeden Request angebracht wird.
  readonly userAgentHeader = `${environment.productTypeVersion}, ${environment.productVersion}, ${environment.productShortname}`;

  readonly sessionService = inject(SessionService);
  readonly settingsService = inject(SettingsService);
  private readonly config = inject(AppConfigService);

  /**
   * Wird gesetzt, sobald ein bewusster Logout läuft ({@link logout}). Unterdrückt {@link forceLogout}:
   * client.logout(true) zerstört das Token serverseitig, wodurch der laufende Sync-Longpoll ein
   * erwartetes 401 M_UNKNOWN_TOKEN erhält. Ohne diesen Guard würde der 401-Handler in
   * {@link createTimFetch} ein forceLogout auslösen, das per location.replace(origin) navigiert und
   * die reguläre Keycloak-Logout-Weiterleitung (location.href) überholt — der User landete dann auf
   * /login, ohne dass die IDP-Session beendet wird. Wird bewusst nicht zurückgesetzt: Ein Logout ist
   * terminal, die Seite navigiert danach weg (Reload → Service-Neuinstanziierung → Flag wieder false).
   */
  private logoutInProgress = false;

  /**
   * Der "globale" Matrix-Client, der den gesamten State des SB-Clients hält.
   * Der Zugriff aus anderen JS-Modulen erfolgt via getClient().
   */
  private readonly clientWithOrigin$ = new BehaviorSubject<ClientWithOrigin>(ClientWithOrigin.logout());
  /**
   * Quick-And-Dirty, damit die Passphrase auch in der Login-Komponente zur Verfügung steht,
   * da es aktuell keine gemeinsame übergeordnete Komponente für die Login- und die Verification-Komponente gibt.
   */
  private verificationPassword: string | undefined;

  private readonly updatePresence = new Subject<void>();
  updatePresence$ = this.updatePresence.asObservable();

  /**
   * Gibt den von diesem JS-Modul verwalteten {@link MatrixClient} zurück.
   * Wirft einen Fehler, falls aktuell kein Client verfügbar ist.
   */
  get client(): MatrixClient {
    const client = this.getClientSync();
    if (!client) {
      throw new Error('Es ist kein Matrix-Client aktiv');
    }
    return client;
  }

  get hasClient(): boolean {
    return !!this.getClientSync();
  }

  getClientWithOrigin$(): Observable<ClientWithOrigin> {
    return this.clientWithOrigin$.asObservable();
  }

  redirectToSSO(host: string): string {
    const tempClient = this.createMatrixClient({
      baseUrl: `https://${host}`,
      fetchFn: this.createTimFetch()
    });
    const redirectUrl = globalThis.location.origin + '/callback';
    return tempClient.getSsoLoginUrl(redirectUrl);
  }

  async loginWithToken(loginToken: string, host: string): Promise<string> {
    const tempClient = this.createMatrixClient({
      baseUrl: `https://${host}`,
      fetchFn: this.createTimFetch()
    });
    const loginResponse = await tempClient.loginRequest({
      type: 'm.login.token',
      refresh_token: true,
      token: loginToken
    });
    const client = await this.initClient(host, loginResponse);
    this.setClientWithOrigin(ClientWithOrigin.login(client));

    return loginResponse.user_id;
  }

  getClientSync(): MatrixClient | null {
    return this.clientWithOrigin$.value.client;
  }

  getUser(userId: string): User {
    if (!userId) {
      throw new Error('userId was null or undefined when calling getUser');
    }
    const client = this.getClientSync();
    if (!client) {
      throw new Error('Module client was null when calling getUser');
    }
    const user = client.getUser(userId);
    if (!user) {
      throw new Error('User was null when calling getUser');
    }
    return user;
  }

  getUserId(): string {
    const client = this.getClientSync();
    if (!client) {
      throw new Error('No module client when calling getUserId');
    }
    const userId = client.getUserId();
    if (!userId) {
      throw new Error('userId was null when calling getUserId');
    }
    return userId;
  }

  /**
   * Hilfsfunktion, die auch ohne {@link client aktiven Client} einen Login an einem beliebigen Homeserver durchführen kann.
   * Dafür wird nur für diesen Request ein neuer {@link MatrixClient} erzeugt.
   * Der Request wird mit dem Header *X-TIM-User-Agent* durchgeführt.
   *
   * @param host Hostname des Homeservers
   * @param user Nutzername des einzuloggenden Users
   * @param pw   Passwort des Users
   * @return user_id of LoginResponse
   */
  async login(host: string, user: string, pw: string): Promise<string> {
    const tempClient = this.createMatrixClient({
      baseUrl: `https://${host}`,
      fetchFn: this.createTimFetch()
    });

    const loginResponse = await tempClient.loginRequest({
      identifier: { user, type: 'm.id.user' },
      password: pw,
      type: 'm.login.password',
      refresh_token: true
    });

    const client = await this.initClient(host, loginResponse);
    this.setClientWithOrigin(ClientWithOrigin.login(client));

    return loginResponse.user_id;
  }

  /**
   * Hilfsfunktion, die den aktuell eingeloggten User ausloggt,
   * die Listener deaktiviert und den Session storage säubert.
   * Stoppt den von diesem JS-Modul verwalteten {@link MatrixClient} und löscht ihn anschließend.
   */
  async logout(): Promise<void> {
    // Ab hier ist jedes 401 M_UNKNOWN_TOKEN selbst verursacht (Token wird zerstört) und darf kein
    // forceLogout auslösen, das die Keycloak-Logout-Weiterleitung überholen würde. Siehe logoutInProgress.
    this.logoutInProgress = true;
    this.deactivateListeners();

    // Ausstehende Key-Backup-Uploads begrenzt abwarten, BEVOR das Token zerstört wird.
    // client.logout(true) bricht laufende Crypto-Uploads hart ab (stopClient + http.abort);
    // ein noch nicht hochgeladener Raum-Schlüssel ginge damit endgültig verloren und die
    // Nachricht wäre nach Re-Login bzw. für föderierte Empfänger nie entschlüsselbar
    // ("The sender's device has not sent us the keys").
    await this.flushPendingKeyBackupUploads();

    // setPresence darf den Logout nicht blockieren — bei 429 M_LIMIT_EXCEEDED oder sonstigen
    // Fehlern des Presence-Servers würde sonst `client.logout(true)` nicht mehr laufen, das
    // Device nicht zerstört und die Session-Storage-Cleanup-Sequenz abgerissen. Der User
    // bliebe im Browser "eingeloggt"; beim nächsten Reload würde restoreSession einen
    // halb-zerstörten Crypto-State laden → OTK-Konflikt (400 M_UNKNOWN "already exists").
    try {
      await this.client.setPresence({ presence: 'offline' });
    } catch (error) {
      console.warn('setPresence(offline) während Logout fehlgeschlagen — ignoriere und fahre fort:', error);
    }

    // client.logout(true) zerstört das Device serverseitig. Transiente Fehler (429 M_LIMIT_EXCEEDED,
    // Netz) werden mehrfach wiederholt, damit kein verwaistes Device zurückbleibt. Scheitert es
    // endgültig, wird der Fehler nur geloggt — die lokale Session wird im finally TROTZDEM
    // aufgeräumt, sonst bliebe ein halb-zerstörter State zurück (Reload → restoreSession →
    // OTK-Konflikt 400 M_UNKNOWN "already exists").
    try {
      await this.logoutClientWithRetry();
    } catch (error) {
      console.warn('client.logout(true) endgültig fehlgeschlagen — Device evtl. nicht gelöscht:', error);
    } finally {
      sessionStorage.clear();
      Settings.setInstance(DEFAULT_CONFIGURATION);
      this.setClientWithOrigin(ClientWithOrigin.logout());
      localStorage.clear();
    }
  }

  /**
   * Baut die Keycloak-Logout-URL aus der App-Konfiguration und leitet den Browser dorthin weiter.
   *
   * Zentraler Weiterleitungspunkt für ALLE Logout-Auslöser (manueller Button UND
   * Inaktivitäts-Abmeldung). Nur so wird die IDP-Session (SSO-Cookie) tatsächlich beendet:
   * Eine bloße Navigation zur App-internen /login-Route lässt das SSO-Cookie bestehen, sodass
   * der IDP den User sofort wieder ohne Passworteingabe anmelden würde — das verletzt
   * A_25507 (Abmeldung statt Sperre) und A_26282 (automatische Abmeldung).
   */
  redirectToIdpLogout(): void {
    const baseUrl = this.config.get('idpBaseUrl');
    const realm = this.config.get('idpRealm');
    const client = this.config.get('idpClient');
    const redirectUrl = `${globalThis.location.origin}/login`;
    const url =
      `${baseUrl}/realms/${realm}/protocol/openid-connect/logout` +
      `?client_id=${client}&post_logout_redirect_uri=${redirectUrl}`;
    this.redirectToHref(url);
  }

  private redirectToHref(url: string): void {
    globalThis.location.href = url;
  }

  /**
   * Ruft `client.logout(true)` auf und wiederholt bei transienten Fehlern (z.B. 429), damit das
   * Device zuverlässig serverseitig gelöscht wird. Wirft erst, wenn alle Versuche scheitern.
   */
  private async logoutClientWithRetry(attempts = 3, delayMs = 500): Promise<void> {
    const client = this.getClientSync();
    if (!client) {
      return;
    }
    for (let attempt = 1; ; attempt++) {
      try {
        await client.logout(true);
        return;
      } catch (error) {
        if (attempt >= attempts) {
          throw error;
        }
        console.warn(`client.logout(true) Versuch ${attempt}/${attempts} fehlgeschlagen, neuer Versuch:`, error);
        await new Promise<void>(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  /**
   * Wartet begrenzt, bis ausstehende Key-Backup-Uploads abgeschlossen sind.
   *
   * Die Rust-Crypto bietet keine Flush-API; einziges Signal ist das Event
   * {@link CryptoEvent.KeyBackupSessionsRemaining} (Anzahl noch zu sichernder Sessions).
   * Heuristik: Feuert innerhalb der Grace-Zeit kein Event, steht nichts aus → sofort weiter.
   * Feuert es mit remaining > 0, wird bis remaining === 0 gewartet — höchstens bis zum
   * harten Timeout, damit der Logout niemals blockiert. Fehler werden verschluckt:
   * Der Logout muss in jedem Fall durchlaufen.
   */
  private async flushPendingKeyBackupUploads(timeoutMs = 10_000, graceMs = 1_000): Promise<void> {
    try {
      const client = this.getClientSync();
      const crypto = client?.getCrypto();
      if (!client || !crypto) return;

      // Ohne aktives Backup gibt es nichts zu flushen.
      if (!(await crypto.getActiveSessionBackupVersion())) return;

      await new Promise<void>(resolve => {
        const onRemaining = (remaining: number): void => {
          clearTimeout(graceTimer); // Es ist Upload-Arbeit im Gange → Grace-Abbruch deaktivieren
          if (remaining === 0) finish();
        };
        const finish = (): void => {
          clearTimeout(graceTimer);
          clearTimeout(hardTimer);
          client.removeListener(CryptoEvent.KeyBackupSessionsRemaining, onRemaining);
          resolve();
        };
        client.on(CryptoEvent.KeyBackupSessionsRemaining, onRemaining);
        const graceTimer = setTimeout(finish, graceMs);
        const hardTimer = setTimeout(finish, timeoutMs);
      });
    } catch (error) {
      console.warn('Key-Backup-Flush vor Logout fehlgeschlagen — ignoriere und fahre fort:', error);
    }
  }

  // TODO Refactoring: Code mit initClient teilen
  async restoreSession(roomId: string): Promise<boolean> {
    const storedSession = this.sessionService.getSessionData();

    if (!storedSession) {
      return false;
    }

    if (roomId?.length) {
      this.sessionService.setSessionRoomId(roomId);
    }

    const client = this.createLocalClient(storedSession.clientOpts);

    const blockTimestamp = Date.now() + 1000 * 60 * 60 * 12;
    client.on(ClientEvent.Sync, (state: SyncState) => {
      // TODO State in Stream (Subject) einspeisen (statt Callback)
      if (Date.now() > blockTimestamp && state === SyncState.Syncing) {
        client.stopClient();
      }
    });

    try {
      if (!client.getCrypto()) {
        await client.initRustCrypto();
        console.info('✅ RustCrypto initialisiert');
      }

      // Konsistenz mit initClient: Raumschlüssel werden an alle (auch unverifizierte) Geräte
      // geteilt. Im Restore-Pfad bewusst KEIN clearStores(), damit der wiederhergestellte
      // Krypto-Store erhalten bleibt.
      const crypto = client.getCrypto();
      if (crypto) {
        crypto.globalBlacklistUnverifiedDevices = false;
      }

      if (storedSession) {
        Settings.setInstance(storedSession.savedSettings); // Setze die gespeicherten Settings
        console.info('Settings aus sessionStorage wiederhergestellt');
      }
      await client.startClient({ disablePresence: true, initialSyncLimit: 100 });
      client.once(ClientEvent.Sync, state => {
        if (state === 'PREPARED') {
          this.requestPresenceUpdate();
        }
      });
      console.info('✅ Session erfolgreich wiederhergestellt!');

      this.setClientWithOrigin(ClientWithOrigin.restoreSession(client));

      return true;
    } catch (error) {
      this.sessionService.clearSessionData();
      console.error('❌ Fehler beim Wiederherstellen der Session:', error);
      return false;
    }
  }

  createTimFetch(): (input: string | Request | URL, init?: RequestInit) => Promise<Response> {
    return async (input: string | Request | URL, init?: RequestInit): Promise<Response> => {
      // init niemals ersetzen, nur um den User-Agent-Header ergänzen. Ein Überschreiben würde
      // andere Felder verwerfen — insbesondere das AbortSignal, über das client.http.abort()
      // (z.B. beim Logout via client.logout(true)) laufende Requests abbricht. Ginge das Signal
      // verloren, liefe der Request weiter und könnte nach der Token-Zerstörung ein
      // 401 M_UNKNOWN_TOKEN fangen und ein ungewolltes forceLogout auslösen.
      init = init ?? {};
      if (init.headers) {
        (init.headers as Record<string, string>)['X-TIM-User-Agent'] = this.userAgentHeader;
      } else {
        init.headers = { 'X-TIM-User-Agent': this.userAgentHeader };
      }
      // Refresh Token fails => logout
      const response = await fetch(input, init);
      if (response.status === 401) {
        try {
          const clone = response.clone();
          const body = await clone.json();
          if (body?.errcode === 'M_UNKNOWN_TOKEN') {
            // Prevent infinite loop
            setTimeout(() => this.forceLogout(), 0);
          }
        } catch {
          console.error('There was a problem, when forcing Logout');
        }
      }

      return response;
    };
  }

  /********************************************************************************************************************
   * Additional Getters/Setters
   ********************************************************************************************************************/

  getVerificationPassword(): string {
    if (!this.verificationPassword) {
      throw new Error('No verification password set');
    }
    return this.verificationPassword;
  }

  /********************************************************************************************************************
   * Hilfsfunktionen
   ********************************************************************************************************************/

  setVerificationPassword(password: string | undefined): void {
    this.verificationPassword = password;
  }

  /********************************************************************************************************************
   * getLiveTimeline (ehemals in RoomService)
   ********************************************************************************************************************/

  getLiveTimeline(roomId: string | undefined): EventTimeline {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling getLiveTimeline.');
    }
    const room = this.client.getRoom(roomId);
    if (!room) {
      throw new Error(`No room was found for roomId ${roomId} when calling getLiveTimeline.`);
    }

    return room.getLiveTimeline();
  }

  protected createMatrixClient(opts: ICreateClientOpts): MatrixClient {
    return createClient(opts);
  }

  private requestPresenceUpdate(): void {
    this.updatePresence.next();
  }

  private redirectTo(url: string): void {
    globalThis.location.replace(url);
  }

  private async forceLogout(): Promise<void> {
    // Läuft bereits ein bewusster Logout, ist das auslösende 401 M_UNKNOWN_TOKEN selbst verursacht.
    // Ein forceLogout (location.replace(origin)) würde hier die Keycloak-Logout-Weiterleitung
    // überholen und den User auf /login schicken, ohne die IDP-Session zu beenden.
    if (this.logoutInProgress) {
      return;
    }

    const client = this.getClientSync();

    try {
      client?.stopClient();
    } catch {
      console.error('There was a Problem when calling forceLogout');
    }

    this.sessionService.clearSessionData();
    sessionStorage.clear();
    localStorage.clear();

    // Hard reload
    this.redirectTo(globalThis.location.origin);
  }

  private setClientWithOrigin(client: ClientWithOrigin): void {
    this.clientWithOrigin$.next(client);
  }

  /**
   * Hilfsfunktion, die die Logik zentralisiert, die von beiden Login-Varianten (Passwort und persistierte Session)
   * geteilt wird.
   * Dazu gehört Folgendes:
   * * Initialisierung der E2EE (via {@link MatrixClient#initRustCrypto}).
   * * Start der Synchronisation (via {@link MatrixClient#startClient}).
   * * Konfiguration der Sperrung des Clients nach 12h.
   *
   * Für die Sperrung wird ein Listener gesetzt, der bei jedem {@link ClientEvent.Sync} Event prüft,
   * ob die Frist abgelaufen ist.
   * Bei abgelaufener Frist wird der Client ausgeloggt oder gesperrt,
   * abhängig davon, ob die Session persistiert wurde.
   *
   * @param host
   * @param response
   */
  private async initClient(host: string, response: RegisterResponse | LoginResponse): Promise<MatrixClient> {
    const clientOpts: ICreateClientOpts = {
      baseUrl: `https://${host}`,
      deviceId: response.device_id,
      userId: response.user_id,
      accessToken: response.access_token,
      refreshToken: response.refresh_token
    };

    const client = this.createLocalClient(clientOpts);

    await client.clearStores();
    await client.initRustCrypto();

    // TODO: Device Verification
    const crypto = client.getCrypto();
    if (crypto) {
      crypto.globalBlacklistUnverifiedDevices = false;
    }
    localStorage.setItem('sidenavState', 'opened');
    this.sessionService.setSessionData({
      clientOpts,
      savedSettings: Settings.getInstance()
    });
    await this.settingsService.loadSettings(client);
    // initialSyncLimit legt fest, wie viele Events pro Raum beim ersten Sync geladen werden.
    // Wichtig: Ohne Limit wird manchmal kein PREPARED gesendet oder der Client bleibt in SYNCING.
    await client.startClient({ disablePresence: true, initialSyncLimit: 100 });
    client.once(ClientEvent.Sync, state => {
      if (state === 'PREPARED') {
        this.requestPresenceUpdate();
      }
    });

    return client;
  }

  private createLocalClient(clientOpts: ICreateClientOpts): MatrixClient {
    SchedulerService.RETRY_BACKOFF();

    return this.createMatrixClient({
      ...clientOpts,
      fetchFn: this.createTimFetch(),
      tokenRefreshFunction: this.timRefresh.bind(this),
      cryptoCallbacks: {
        getSecretStorageKey: this.customGetSecretStorageKey
      },
      verificationMethods: [VerificationMethod.Sas],
      scheduler: new MatrixScheduler()
    });
  }

  /**
   * Hilfsfunktion, die einen Refresh des aktiven Access-Tokens durchführt.
   * (In Default-Einstellungen ist die Refresh-Funktion des {@link MatrixClient}s *NULL*
   * und der Refresh kann nicht durchgeführt werden.)
   *
   * @param token Refreshtoken, mit dem der Refresh durchgeführt werden soll.
   * @private
   */
  private async timRefresh(token: string): Promise<AccessTokens> {
    const client = this.getClientSync();
    if (!client) {
      this.sessionService.clearSessionData();
      throw new Error('Refresh-Function darf nur aufgerufen werden, während ein Client aktiv ist');
    }
    try {
      const resp = await client.refreshToken(token);
      // Updating session token for client after token refresh
      const session = this.sessionService.getSessionData();
      if (session) {
        session.clientOpts.accessToken = resp?.access_token;
        session.clientOpts.refreshToken = resp?.refresh_token;
        this.sessionService.setSessionData(session);
      }

      return {
        accessToken: resp.access_token,
        refreshToken: resp.refresh_token
      };
    } catch (error) {
      console.error('🔴 Refresh token failed:', error);

      await this.forceLogout();

      throw error;
    }
  }

  /**
   * Krypto-Callback zur Abfrage des sog. Secret-Storage-Schlüssels, der für die verschlüsselte Ablage von Secrets im
   * Secure-Secret-Storage des Homeservers genutzt wird. Der Schlüssel wird dabei mithilfe einer Passphrase, die vom
   * Benutzer manuell eingegeben worden sein muss, sowie Metainformationen, die im Secret-Storage verfügbar sind,
   * abgeleitet. Potenziell können auch mehrere Secret-Storage-Schlüssel verwendet werden. An dieser Stelle wird dies
   * jedoch nicht unterstützt und entsprechende Anfragen mit einem Fehler quittiert.
   * @param keyInfos
   */
  private readonly customGetSecretStorageKey: SecretStorageCallbacks['getSecretStorageKey'] = async ({
    keys: keyInfos
  }): Promise<[string, Uint8Array<ArrayBuffer>]> => {
    const localClient = this.client;

    let keyId = await localClient.secretStorage.getDefaultKeyId();
    let keyInfo!: SecretStorage.SecretStorageKeyDescription;
    if (keyId) {
      // use the default SSSS key if set
      keyInfo = keyInfos[keyId];
      if (!keyInfo) {
        // if the default key is not available, pretend the default key
        // isn't set
        keyId = null;
      }
    }
    if (!keyId) {
      // if no default SSSS key is set, fall back to a heuristic of using the
      // only available key, if only one key is set
      const keyInfoEntries = Object.entries(keyInfos);
      if (keyInfoEntries.length > 1) {
        throw new Error('Multiple storage key requests not implemented');
      }
      [keyId, keyInfo] = keyInfoEntries[0];
    }

    const localVerificationPassword = this.verificationPassword;
    if (!localVerificationPassword) {
      throw new Error('Kein Passwort zur Verifizierung verfügbar');
    }
    const uint8Array = await this.deriveKey(
      localVerificationPassword,
      keyInfo.passphrase.salt,
      keyInfo.passphrase.iterations
    );

    // decodeRecoveryKey(recoveryKey); falls nicht die Passphrase, sondern der RecoveryKey genutzt werden soll
    const checkKeyResult = await localClient.secretStorage.checkKey(uint8Array, keyInfo);
    if (!checkKeyResult) {
      throw new Error('Abgeleiteter Schlüssel ' + keyInfo.name + ' entspricht nicht den Erwartungen');
    }

    return [keyId, uint8Array];
  };

  private async deriveKey(passphrase: string, salt: string, iterations: number): Promise<Uint8Array<ArrayBuffer>> {
    const enc = new TextEncoder();
    const keyMaterial = await globalThis.crypto.subtle.importKey(
      'raw',
      enc.encode(passphrase),
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    const derivedBits = await globalThis.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: enc.encode(salt),
        iterations,
        hash: 'SHA-512'
      },
      keyMaterial,
      256
    );
    return new Uint8Array<ArrayBuffer>(derivedBits);
  }

  private deactivateListeners(): void {
    // getClientSync() statt des werfenden client-Getters: deactivateListeners() läuft als erste
    // Zeile in logout() vor jedem try/catch. Ist kein Client (mehr) aktiv, würde der Getter hier
    // werfen und den gesamten Logout inkl. Storage-Cleanup und IDP-Weiterleitung abbrechen.
    const client = this.getClientSync();
    if (!client) {
      return;
    }
    client.removeAllListeners(RoomEvent.Timeline);
    client.removeAllListeners(MatrixEventEvent.Decrypted);
    client.removeAllListeners(RoomEvent.LocalEchoUpdated);
    client.removeAllListeners(RoomEvent.Receipt);
  }
}

/** @deprecated (Testcode - bitte anders lösen!) */
export interface LoggedInUser {
  mxid: string;
}
